import React, { useEffect, useRef, useState } from "react";
import { withTranslation, Trans, useTranslation } from "react-i18next";
import validateInput from "@cores/validateInput";
import { LIMIT_CHAR } from "@constants";
import deepEqual from "deep-equal";
import Typography from "@components_v2/typography/typography";
import TextField from "@components_v2/text-field/text-field";
import { Button, Grid, IconButton } from "@mzc-pdc/ui";
import { IconAdd, IconCancel, IconDelete } from "@mzc-cloudplex/icons";
import { Icon } from "@mui/material";
import uniqid from "uniqid";
import theme from "@styles/theme";

const SearchAttributionsInput = ({ type, defaultValues, onChangeAttributions }) => {
    const { t } = useTranslation();
    const [values, setValues] = useState([{ id: uniqid(), key: null, value: null }]);
    const [validationErrors, setValidationErrors] = useState({});

    const validate = (str, values, isKey) => {
        const validationError = validateInput(str, {
            //required: true,
            maxLength: LIMIT_CHAR.attribution,
            uniqueIn: isKey && values,
        });

        if (validationError && validationError.type === "REQUIRED") {
            validationError.level = "warning";
        }

        return validationError;
    };

    const getValidationError = (key) => {
        return validationErrors[key] || {};
    };

    const updateOtherKeys = (id, keys) => {
        let updateTarget = {};

        values?.forEach(({ id: keyId, key }) => {
            if (keyId !== id) {
                const validationError = validate(key, keys, true);
                Object.assign(updateTarget, { [`attribution-${keyId}-key`]: validationError });
            }
        });

        return updateTarget;
    };

    const onHandleChangeKey = (id, key) => {
        const newValues = values.map(({ id: valueId, key: k, value: v }) => ({
            id: valueId,
            key: valueId === id ? key : k,
            value: v,
        }));

        const keys = newValues.map((v) => v.key);

        let updateTarget = updateOtherKeys(id, keys);
        const validationError = validate(key, keys, true);

        Object.assign(updateTarget, { [`attribution-${id}-key`]: validationError });
        const newValidationErrors = { ...validationErrors, ...updateTarget };

        setValues(newValues);
        setValidationErrors(newValidationErrors);

        onHandleChangeAttributions(newValues, newValidationErrors);
    };

    const onHandleChangeValue = (id, value) => {
        const newValues = values.map(({ id: valueId, key: k, value: v }) => ({
            id: valueId,
            key: k,
            value: valueId === id ? value : v,
        }));

        setValues(newValues);
        onHandleChangeAttributions(newValues);
    };

    const onHandleClickAddAttribution = () => {
        const id = uniqid();

        const newAttribution = { id, key: null, value: null };
        setValues([...values, newAttribution]);

        onHandleChangeAttributions([...values, newAttribution]);
    };

    const onHandleClickRemoveAttribution = (id) => {
        const newValidationErrors = { ...validationErrors };
        const newValues = values.filter((value) => value.id !== id);

        const validationErrorKeys = Object.keys(newValidationErrors);
        if (validationErrorKeys.length > 0) {
            validationErrorKeys.forEach((k) => {
                if (k.indexOf(`attribution-${id}`) > -1) {
                    delete newValidationErrors[k];
                }
            });
        }

        setValues(newValues);
        setValidationErrors(newValidationErrors);
        onHandleChangeAttributions(newValues, newValidationErrors);
    };

    const onHandleChangeAttributions = (val, errors) => {
        const parsedValidationErrors = { ...errors };
        Object.keys(parsedValidationErrors ?? {}).forEach(
            (k) => parsedValidationErrors[k] == null && delete parsedValidationErrors[k],
        );

        onChangeAttributions(val, parsedValidationErrors);
    };

    useEffect(() => {
        if (defaultValues?.length > 0) {
            let newValidationErrors = {};
            let newValues = [];

            defaultValues.forEach((value) => {
                const id = uniqid();

                newValues.push({ id, key: value.key, value: value.value });

                newValidationErrors[`attribution-${id}-key`] = validate(
                    value.key,
                    defaultValues.map(({ key }) => key),
                    true,
                );

                newValidationErrors[`attribution-${id}-value`] =
                    type !== "METADATA_TEMPLATE" ? validate(value.value) : undefined;
            });

            setValues(newValues);
            setValidationErrors(newValidationErrors);
        } else if (values.find((value) => value.key || value.value) !== null) {
            setValues([{ id: uniqid(), key: null, value: null }]);
            setValidationErrors({});
        }
    }, [defaultValues]);

    return (
        <Grid container flexDirection={"column"} gap={2}>
            {values?.map((v, i) => (
                <Grid key={i} container gap={1} flexDirection={"row"} justifyContent={"space-between"}>
                    <Grid item sx={{ flexGrow: 1, maxWidth: "44%" }}>
                        <TextField
                            value={v.key ?? ""}
                            placeholder={t(`common::label::${"Key"}`)}
                            id={"key" + v.id}
                            label={"Key"}
                            onChange={(e) => {
                                onHandleChangeKey(v.id, e.target.value);
                            }}
                            error={getValidationError(`attribution-${v.id}-key`).level}
                            //helperText={getValidationError(`attribution-${v.id}-key`).message}
                        />
                    </Grid>

                    <Grid item sx={{ flexGrow: 1, maxWidth: "44%" }}>
                        <TextField
                            value={v.value ?? ""}
                            placeholder={t(`common::label::${"Value"}`)}
                            label={"Value"}
                            id={"value" + v.id}
                            onChange={(e) => {
                                onHandleChangeValue(v.id, e.target.value);
                            }}
                            error={getValidationError(`attribution-${v.id}-value`).level}
                            //helperText={getValidationError(`attribution-${v.id}-value`).message}
                        />
                    </Grid>

                    <Grid item sx={{ display: "flex", alignItems: "center" }}>
                        {i !== values.length - 1 ? (
                            <IconButton
                                onClick={() => {
                                    onHandleClickRemoveAttribution(v.id);
                                }}
                                sx={{ height: "40px", width: "40px" }}
                            >
                                <IconCancel size={16} />
                            </IconButton>
                        ) : (
                            <IconButton
                                variant={"outlined"}
                                disabled={Object.values(validationErrors).filter((v) => v).length > 0}
                                onClick={(e) => {
                                    onHandleClickAddAttribution();
                                }}
                                sx={{ height: "40px", width: "40px" }}
                            >
                                <IconAdd size={20} />
                            </IconButton>
                        )}
                    </Grid>
                </Grid>
            ))}
        </Grid>
    );
};

export default SearchAttributionsInput;
